/**
 * The first screen of the visit: a real Linux is starting inside this tab.
 *
 * A first visit downloads roughly thirteen megabytes of saved machine state
 * and then restores it, which is fast (1.7 s warm in the phase 0 numbers,
 * docs/browser.md) but is not instant and is not something a learner has any
 * reason to expect. So the wait is narrated rather than spun: what is
 * happening, how many bytes of how many, and the fact that none of it leaves
 * the machine. The steps are the same four the VM reports, in the order they
 * happen, drawn the way objectives are drawn on the play screen — filled for
 * done, hollow for now, dim for later — so the vocabulary is one vocabulary.
 *
 * Green appears on the things that are live right now, the bar that is
 * filling and the step that is running, and on the wordmark and the mark's
 * caret, where it always is. Nothing else on a black screen competes with them.
 *
 * The lockup comes up the way the machine does, once per session: the tile
 * settles, the chevron draws, the caret rises and goes live, the wordmark
 * wipes in, and only then does the narration fade up (motion.css §1). The
 * timing lives in the stylesheet; this file only names the parts — `tile` and
 * `live` on the mark, `boot-status` around the narration. A failure sits
 * outside that wrapper on purpose: an error that arrives after the screen is
 * up has to appear at once, not wait its turn behind a fade.
 *
 * This component is presentational. `App` owns the boot itself, because it is
 * the thing that has to know when the workstation may be shown. It keeps this
 * screen mounted across a retry, which is what makes the sequence once per
 * session rather than once per attempt.
 */
import { getStrings, type Strings } from '../lib/strings';
import { useStrings } from '../state/useLocale';
import { Button, Mark } from '../ui';

/**
 * Structurally `VmProgress` from `@engineer-os/vm`, restated here so the
 * client's boot screen does not have to depend on the VM package to draw a
 * progress bar. If the two ever diverge, the assignment in `App` stops
 * compiling, which is where the mismatch belongs.
 */
export type BootPhase = 'manifest' | 'assets' | 'restore' | 'boot' | 'warm' | 'ready';

export interface BootProgress {
  phase: BootPhase;
  /** 0–1 across the whole boot, or null while nothing knows the total. */
  fraction: number | null;
  bytesLoaded: number;
  bytesTotal: number;
  /**
   * One short line, already written for a human — and, after the first one,
   * written by the VM package in English (`report(…)` in packages/vm/src/
   * boot.ts). `FIRST_PROGRESS.message` below is ours and is translated; the
   * four sentences the VM sends after it cannot be from here, because what
   * arrives is a sentence rather than a phase to write one from. The step
   * list under this line says the same four things in the learner's language,
   * which is what keeps the gap from being the only narration they get.
   */
  message: string;
}

/** What went wrong, in the VM package's own vocabulary. */
export interface BootFailure {
  code: string;
  /** The raw message. Shown small, under the sentence, for a bug report. */
  detail: string;
}

export const FIRST_PROGRESS: BootProgress = {
  phase: 'manifest',
  fraction: null,
  bytesLoaded: 0,
  bytesTotal: 0,
  /**
   * A getter, not a value. This object is a module constant that `App` puts
   * straight into state, so a plain string would be read once, at import,
   * and would still be in that language after the learner used the switch.
   * Every other line on this screen comes from the VM and is replaced within
   * a frame; this one can sit on screen for as long as the first fetch takes.
   */
  get message(): string {
    return getStrings().boot.firstMessage;
  },
};

/* The four steps a learner sees, and which VM phases belong to each. `restore`
   and `boot` are one step: from outside they are the same wait, and which one
   happens depends on whether a saved state shipped, which is our business and
   not theirs. The label is the catalogue's; the key is what looks it up, so a
   step here with no sentence there does not compile. */
const STEPS: { key: keyof Strings['boot']['steps']; phases: BootPhase[] }[] = [
  { key: 'manifest', phases: ['manifest'] },
  { key: 'assets', phases: ['assets'] },
  { key: 'start', phases: ['restore', 'boot'] },
  { key: 'warm', phases: ['warm', 'ready'] },
];

function stepState(step: (typeof STEPS)[number], phase: BootPhase): 'done' | 'now' | 'todo' {
  const here = STEPS.findIndex((s) => s.phases.includes(phase));
  const mine = STEPS.indexOf(step);
  if (mine < here) return 'done';
  return mine === here ? 'now' : 'todo';
}

export interface BootScreenProps {
  progress: BootProgress;
  /** Non-null once the boot has failed; the steps give way to the reason. */
  error: BootFailure | null;
  onRetry(): void;
}

export function BootScreen({ progress, error, onRetry }: BootScreenProps) {
  const s = useStrings().boot;
  const pct = progress.fraction === null ? null : Math.round(Math.max(0, Math.min(1, progress.fraction)) * 100);
  const bytes = progress.bytesTotal > 0 ? s.bytes(progress.bytesLoaded, progress.bytesTotal) : null;
  // The VM's own vocabulary, looked up in ours. A code we have no sentence
  // for still gets one, because "" is not an answer to a black screen.
  const reasons: Record<string, string | undefined> = s.failure.reason;
  const reason = error ? (reasons[error.code] ?? s.failure.fallback) : null;

  return (
    <div className="boot">
      <div className="boot-inner">
        <p className="boot-brand">
          <Mark tile live />
          <span className="brand-word">
            Engineer<b>OS</b>
          </span>
        </p>

        {error ? (
          // Not inside .boot-status: a failure that arrives after the screen
          // is up has to appear at once, not wait behind the narration's fade.
          <>
            <h1 className="rail-title">{s.failure.heading}</h1>
            <p className="lede">{reason}</p>
            <p className="boot-actions">
              <Button variant="solid" onClick={onRetry}>
                {s.failure.retry}
              </Button>
            </p>
            {/* The raw message, small and dim: useless to most people, and the
                first thing anyone reporting a bug will be asked for. It is the
                VM's own English and stays that way in both languages, so it is
                drawn left to right — an asset path or a status line reordered
                by bidi is a bug report nobody can act on (docs/i18n.md §7). */}
            <p className="note boot-detail" dir="ltr">
              {error.detail}
            </p>
          </>
        ) : (
          <div className="boot-status">
            <h1 className="rail-title">{s.heading}</h1>
            <p className="lede">{s.lede}</p>

            <div
              className="boot-bar"
              role="progressbar"
              aria-label={s.barLabel}
              aria-valuemin={0}
              aria-valuemax={100}
              {...(pct === null ? {} : { 'aria-valuenow': pct })}
            >
              <div
                className={pct === null ? 'boot-bar-fill is-waiting' : 'boot-bar-fill'}
                style={pct === null ? undefined : { width: `${pct}%` }}
              />
            </div>

            {/* Live, because on a slow connection this line is the only thing
                that changes for several seconds. */}
            <p className="boot-line" role="status" aria-live="polite">
              {/* `dir="auto"`, because this one span is the only place in the
                  product where the language of the text is genuinely not
                  known at render: the first sentence is ours and translated,
                  every one after it is the VM package's and is English in
                  both languages (see `BootProgress.message` above). Left to
                  inherit, an English sentence in an Arabic document has its
                  trailing `…` resolved to the paragraph direction and drawn
                  at the far end of the line — the learner reads
                  "…Fetching the saved machine state". `auto` takes the
                  direction from the first strong character, which is right
                  for both. */}
              <span dir="auto">{progress.message}</span>
              {bytes && <span className="boot-bytes">{bytes}</span>}
            </p>

            <ol className="boot-steps">
              {STEPS.map((step) => {
                const state = stepState(step, progress.phase);
                return (
                  <li key={step.key} className={`boot-step is-${state}`}>
                    <span className="boot-step-mark" aria-hidden="true" />
                    <span>{s.steps[step.key]}</span>
                  </li>
                );
              })}
            </ol>

            <p className="note boot-note">{s.note}</p>
          </div>
        )}
      </div>
    </div>
  );
}
